import type { BlockContentMap } from "@veo/types";
import { cn } from "@veo/ui";
import { asTestimonials } from "../lib/section-data";
import { RevealItem, RevealList } from "../components/motion";
import {
  SectionHeading,
  SectionShell,
  cardHover,
  isDarkBackground,
  type SectionProps,
} from "./SectionRenderer";

/**
 * Client quotes.
 *
 * Only testimonials with recorded consent are ever returned by the API, so
 * consent is enforced at the query rather than trusted to this component.
 */
export default function TestimonialsBlock({
  content,
  settings,
  data,
}: SectionProps<BlockContentMap["testimonials"]>) {
  const onDark = isDarkBackground(settings);
  const all = asTestimonials(data) ?? [];

  // An explicit selection keeps the editor's order. Otherwise the API's own
  // order applies, which already puts featured testimonials first.
  const testimonials =
    content.testimonialIds.length > 0
      ? content.testimonialIds
          .map((id) => all.find((entry) => entry.id === id))
          .filter((entry): entry is (typeof all)[number] => Boolean(entry))
      : all;

  // Nothing to show renders nothing at all: an empty quoted section looks
  // broken on a live page.
  if (testimonials.length === 0) return null;

  return (
    <SectionShell settings={settings}>
      <SectionHeading eyebrow={content.eyebrow} heading={content.heading} settings={settings} />

      <RevealList className={cn("grid gap-6", content.columns === 1 ? "" : "md:grid-cols-3")}>
        {testimonials.slice(0, content.columns === 1 ? 1 : 6).map((testimonial) => (
          <RevealItem key={testimonial.id}>
            <figure
              className={cn(
                "flex h-full flex-col rounded-lg p-7",
                cardHover(onDark),
                onDark ? "bg-navy-800" : "bg-white shadow-card",
              )}
            >
              <blockquote
                className={cn(
                  "flex-1 font-display text-lg italic leading-relaxed",
                  onDark ? "text-cream-50" : "text-navy-800",
                )}
              >
                &ldquo;{testimonial.quote}&rdquo;
              </blockquote>
              <figcaption className={cn("mt-5 text-sm", onDark ? "text-navy-300" : "text-ink-500")}>
                <span className="font-semibold">{testimonial.authorName}</span>
                {testimonial.authorTitle && <span> · {testimonial.authorTitle}</span>}
                {testimonial.authorCompany && <span> · {testimonial.authorCompany}</span>}
              </figcaption>
            </figure>
          </RevealItem>
        ))}
      </RevealList>
    </SectionShell>
  );
}
